import { useState } from "react";
import { api } from "./api/index.js";
import { useResource } from "./api/useResource.js";
import { describeFailure, type ActionFailure } from "./api/errors.js";
import { AuthenticationRecoveryButton } from "./AuthenticationRecoveryButton.js";
import { useCan } from "./session.js";

/**
 * 動画の中身を確認する。
 *
 * 二者承認は「別の人が内容を確認する」ための仕組みなので、**中身を見られない
 * と制度として機能しない**。承認画面から使えることを主目的にする。
 *
 * 署名付きURLはbearer tokenと同じ重みを持つ。押したときだけ取りに行き、
 * 保存もログ出力もしない。
 */

function expiresLabel(value: string): string {
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

export function MediaPreview({
  mediaId,
  name,
}: {
  mediaId: string;
  name: string;
}) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        className="secondary-button"
        onClick={() => {
          setOpen(true);
        }}
      >
        内容を確認する
      </button>
    );
  }

  return (
    <div className="media-preview">
      <div className="media-preview__head">
        <b>{name}</b>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
          }}
        >
          閉じる
        </button>
      </div>
      <PreviewPlayer mediaId={mediaId} />
    </div>
  );
}

function PreviewPlayer({ mediaId }: { mediaId: string }) {
  const preview = useResource(
    (signal) => api.getMediaPreview(mediaId, signal),
    [mediaId],
  );

  if (preview.state === "loading") {
    return <p className="resource-note">読み込み中…</p>;
  }
  if (preview.state === "error") {
    return (
      <div className="inline-alert inline-alert--error">
        <b>再生できません</b>
        <span>{preview.message}</span>
        {preview.code === "NOT_FOUND" && (
          <span>
            この動画には再生できる実体がありません。送信が完了していないか、
            検査で拒否された可能性があります。
          </span>
        )}
      </div>
    );
  }

  const { media, downloadUrl, downloadUrlExpiresAt } = preview.value;

  return (
    <>
      {/* 音声ありの動画は既定で消音にしない。承認者が音を確認するためである。 */}
      <video src={downloadUrl} controls preload="metadata" playsInline />
      <dl className="media-preview__meta">
        <div>
          <dt>解像度</dt>
          <dd>
            {media.width === null || media.height === null
              ? "—"
              : `${String(media.width)}×${String(media.height)}`}
          </dd>
        </div>
        <div>
          <dt>長さ</dt>
          <dd>
            {media.durationMs === null
              ? "—"
              : `${(media.durationMs / 1000).toFixed(1)} 秒`}
          </dd>
        </div>
        <div>
          <dt>音声</dt>
          <dd>{media.hasAudio ? "あり" : "なし"}</dd>
        </div>
      </dl>
      <p className="resource-note">
        {media.hasAudio && (
          <b>この動画には音声があります。現地で音が出ます。</b>
        )}
        表示用のURLは {expiresLabel(downloadUrlExpiresAt)} に切れます。共有せず、
        必要なら開き直してください。
      </p>
    </>
  );
}

/**
 * 動画を撤去する。
 *
 * 物理削除はしない。過去のReleaseから参照され続けるため、消すと操作履歴から
 * 何を配信したか辿れなくなる。一覧から外れるだけである。
 */
export function ArchiveMedia({
  mediaId,
  name,
  onArchived,
}: {
  mediaId: string;
  name: string;
  onArchived: () => void;
}) {
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [failure, setFailure] = useState<ActionFailure | null>(null);
  const [running, setRunning] = useState(false);
  const allowed = useCan("content:write");

  async function archive() {
    setRunning(true);
    try {
      await api.archiveMedia(mediaId, { reason: reason.trim() });
      setConfirming(false);
      setReason("");
      setFailure(null);
      onArchived();
    } catch (error) {
      setFailure(describeFailure(error));
    } finally {
      setRunning(false);
    }
  }

  if (!confirming) {
    return (
      <button
        type="button"
        className="secondary-button"
        disabled={!allowed}
        onClick={() => {
          setConfirming(true);
        }}
      >
        撤去する
      </button>
    );
  }

  return (
    <div className="inline-alert">
      <b>{name} を撤去します</b>
      <span>
        一覧から外れ、新しいプレイリストへ追加できなくなります。
        <strong>すでに公開済みのReleaseからは参照され続けます。</strong>
        いま流れている内容は変わりません。
      </span>
      <label className="field">
        <span>理由（監査ログへ残ります）</span>
        <input
          type="text"
          value={reason}
          placeholder="例: 掲載期間が終了したため"
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
      </label>
      {failure !== null && (
        <>
          <span>{failure.message}</span>
          <AuthenticationRecoveryButton failure={failure} />
        </>
      )}
      <div className="editor-actions">
        <button
          type="button"
          className="danger-button"
          disabled={reason.trim().length === 0 || running}
          onClick={() => void archive()}
        >
          {running ? "撤去中…" : "撤去する"}
        </button>
        <button
          type="button"
          onClick={() => {
            setConfirming(false);
          }}
        >
          やめる
        </button>
      </div>
    </div>
  );
}
